import { inject, Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { BaseService } from '../../../base.service';
import { ApiService } from '../api';

enum StorageKey {
  token = 'celebhere-admin.auth.token',
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  get accessToken(): string | null {
    return this.accessTokenSubject.value;
  }
  get userId(): string | null {
    return this.userIdSubject.value;
  }
  get authed(): boolean {
    return this.userIdSubject.value != null;
  }

  readonly accessToken$: Observable<string | null>;
  readonly userId$: Observable<string | null>;
  readonly authed$: Observable<boolean>;

  private readonly storage = inject(Storage);
  private readonly apiService = inject(ApiService);

  private readonly accessTokenSubject = this.completeOnDestroy(new BehaviorSubject<string | null>(null));
  private readonly userIdSubject = this.completeOnDestroy(new BehaviorSubject<string | null>(null));

  constructor() {
    super();

    this.accessToken$ = this.accessTokenSubject;
    this.userId$ = this.userIdSubject.pipe(distinctUntilChanged());
    this.authed$ = this.userIdSubject.pipe(
      distinctUntilChanged(),
      map((userId) => userId != null)
    );
  }

  async init(): Promise<void> {
    const accessToken = (await this.storage.get(StorageKey.token)) ?? null;
    await this.setAccessToken(accessToken);
  }

  signIn(email: string, password: string): Observable<void> {
    return this.apiService.postAuthSignIn({ email, password }).pipe(switchMap((res) => this.setAccessToken(res.result.token)));
  }

  signOut(): Observable<void> {
    return this.apiService.postAuthSignOut().pipe(
      catchError((err) => {
        if (
          err instanceof Error &&
          ['세션 토큰이 유효하지 않습니다.', '세션을 찾을 수 없습니다.', '세션이 만료되었습니다.', 'invalid signature'].includes(err.message)
        ) {
          return of(undefined);
        }
        return throwError(() => err);
      }),
      switchMap(() => this.setAccessToken(null))
    );
  }

  private async setAccessToken(accessToken: string | null): Promise<void> {
    this.accessTokenSubject.next(accessToken);
    try {
      const jwtPayloadString = accessToken?.split('.')[1] ?? null;
      const jwtPayload = jwtPayloadString != null ? (JSON.parse(atob(jwtPayloadString)) as { sub?: string }) : null;
      const userId = jwtPayload?.sub ?? null;
      await this.setUserId(userId);
    } catch (err) {
      await this.setUserId(null);
      console.error(err);
    }
    if (accessToken != null) {
      await this.storage.set(StorageKey.token, accessToken);
    } else {
      await this.storage.remove(StorageKey.token);
    }
  }

  private async setUserId(userId: string | null): Promise<void> {
    this.userIdSubject.next(userId);
  }
}
